import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Upload, File as FileIcon, X, Sparkles } from "lucide-react";
import { uploadFile, startAnalysis, checkAnalysisResult, chatWithAI, getSessionId, setApiBaseUrl } from '../services/api';

// UI components imports
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';

const InsightCard = ({ insight }) => {
  // Map the impact to appropriate badge colors
  const impactColors = {
    positive: "bg-green-100 text-green-800 hover:bg-green-100",
    negative: "bg-red-100 text-red-800 hover:bg-red-100",
    needs_attention: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
  };

  // Translate impact labels to Turkish
  const impactLabels = {
    positive: "Pozitif",
    negative: "Negatif",
    needs_attention: "Dikkat Gerekli"
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg font-medium">
            {insight.important && (
              <span className="mr-2 inline-block w-2 h-2 bg-red-500 rounded-full"></span>
            )}
            Bulgu
          </CardTitle>
          <Badge className={impactColors[insight.impact] || "bg-gray-100"}>
            {impactLabels[insight.impact] || "Nötr"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-700">{insight.message}</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {insight.tags.map((tag, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {tag.replace('_', ' ')}
            </Badge>
          ))}
        </div>
      </CardContent>
      <CardFooter className="pt-0 text-xs text-gray-500 italic">
        {insight.reasoning}
      </CardFooter>
    </Card>
  );
};

const ChatMessage = ({ message }) => {
  return (
    <div className="mb-6">
      {/* Only show the human message if it exists */}
      {message.human && (
        <div className="mb-3">
          <div className="font-semibold mb-1">Siz:</div>
          <div className="p-3 rounded-lg bg-blue-50">
            <p>{message.human}</p>
          </div>
        </div>
      )}
      
      {/* Only show the AI message if it exists */}
      {message.ai && (
        <div>
          <div className="font-semibold mb-1">Yapay Zeka Asistanı:</div>
          <div className="p-3 rounded-lg bg-gray-50">
            <p className="whitespace-pre-line">{message.ai}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function SalesAnalysisDashboard({ apiBaseUrl = 'http://localhost:8000' }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [insights, setInsights] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState("");
  
  // Reference to store the current job ID and polling interval
  const jobRef = useRef({ 
    jobId: null, 
    pollingInterval: null,
    pollCount: 0,
    maxPolls: 100 // Prevent infinite polling (100 * 2000ms = ~3.3 minutes max)
  });
  
  // Reference to the messages container for auto-scrolling
  const messagesEndRef = useRef(null);

  // Set the API base URL and initialize the session when the component mounts
  useEffect(() => {
    try {
      setApiBaseUrl(apiBaseUrl);
      const sid = getSessionId();
      setSessionId(sid);
      console.log("Session initialized with ID:", sid);
    } catch (err) {
      console.error("Failed to initialize session:", err);
      setError("Oturum başlatılamadı. Lütfen sayfayı yenileyin.");
    }
    
    // Cleanup function to clear any polling intervals when component unmounts
    return () => {
      if (jobRef.current.pollingInterval) {
        clearInterval(jobRef.current.pollingInterval);
      }
    };
  }, [apiBaseUrl]);
  
  // Add a debug effect to log chat message changes
  useEffect(() => {
    console.log("Chat messages updated:", chatMessages);
    
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const switchToChat = () => {
    setActiveTab("chat");
  };

  // Function to start polling for analysis results
  const startPollingAnalysisResult = (jobId) => {
    // Clear any existing polling interval
    if (jobRef.current.pollingInterval) {
      clearInterval(jobRef.current.pollingInterval);
    }
    
    // Reset poll count
    jobRef.current.pollCount = 0;
    jobRef.current.jobId = jobId;
    
    // Set up polling interval
    jobRef.current.pollingInterval = setInterval(async () => {
      try {
        // Increment poll count
        jobRef.current.pollCount++;
        
        // If we've reached max polls, stop polling
        if (jobRef.current.pollCount > jobRef.current.maxPolls) {
          clearInterval(jobRef.current.pollingInterval);
          setAnalyzing(false);
          setError("Analiz çok uzun sürdü. Lütfen daha sonra tekrar deneyin.");
          return;
        }
        
        // Get current progress percentage for UI (caps at 95% until complete)
        const progressIncrement = 95 / jobRef.current.maxPolls;
        const newProgress = Math.min(95, progressIncrement * jobRef.current.pollCount);
        setAnalysisProgress(newProgress);
        
        // Check job status
        const result = await checkAnalysisResult(jobId);
        
        if (result.status === "done" && result.result) {
          // Analysis complete, clear interval and update state
          clearInterval(jobRef.current.pollingInterval);
          setAnalyzing(false);
          setAnalysisProgress(100);
          setInsights(result.result);
          
          // Switch to insights tab
          setTimeout(() => {
            setActiveTab("insights");
          }, 1000);
        }
        // If still pending, continue polling
      } catch (error) {
        console.error("Error polling for analysis result:", error);
        clearInterval(jobRef.current.pollingInterval);
        setAnalyzing(false);
        setError("Analiz sonuçları alınamadı. Lütfen tekrar deneyin.");
      }
    }, 2000); // Check every 2 seconds
  };

  const handleLoadSampleData = async () => {
    try {
      const response = await fetch("/Book2.xlsx");

      console.log("Status:", response.status);
      console.log("Content-Type:", response.headers.get("content-type"));

      if (!response.ok) throw new Error("Dosya bulunamadı");
      const blob = await response.blob();

      console.log("Blob size:", blob.size, "bytes");
      console.log("Blob type:", blob.type);

      // Eğer blob çok küçükse (< 1KB) veya type html ise dosya yok demektir
      if (blob.size < 1000 || blob.type.includes("html")) {
        throw new Error(`Geçersiz dosya — size: ${blob.size}, type: ${blob.type}`);
      }

      const sampleFile = new window.File([blob], "Book2.xlsx", {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        lastModified: Date.now(),
      });

      setFile(sampleFile);

      // Elle yüklemedeki gibi backend'e upload et
      setUploading(true);
      setError(null);

      await uploadFile(sampleFile);
      setUploading(false);
      console.log("Örnek dosya başarıyla yüklendi");
    } catch (err) {
      console.error("Örnek veri yüklenemedi:", err);
      setUploading(false);
      setError("Örnek veri yüklenemedi. Lütfen tekrar deneyin.");
    }
  };

  const handleUploadFile = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    setAnalysisProgress(5); // Start progress at 5%
    setError(null);
    
    try {
      // Start analysis with default questions
      const analysisJob = await startAnalysis();
      
      if (analysisJob && analysisJob.job_id) {
        // Start polling for results
        startPollingAnalysisResult(analysisJob.job_id);
      } else {
        throw new Error("Geçersiz analiz işi yanıtı");
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalyzing(false);
      setAnalysisProgress(0);
      setError("Analiz sırasında bir hata oluştu. Lütfen sayfayı yenileyip tekrar deneyin.");
    }
  };

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;
    
    // Store the current message before clearing the input
    const currentMessage = userMessage;
    
    // Create a message object with only the human message
    const newMessage = { human: currentMessage };
    
    // Add the message to the chat history
    setChatMessages(prev => [...prev, newMessage]);
    
    // Clear the input field
    setUserMessage("");
    
    // Show loading indicator
    setChatLoading(true);
    setError(null);
    
    try {
      // Send message to chat API
      const data = await chatWithAI(currentMessage);
      
      if (data && data.messages && data.messages.length > 0) {
        // Important: Check if API returns multiple messages or just the latest one
        if (data.messages.length === 1) {
          // Simple case: API returns just the latest message
          const apiResponse = data.messages[0];
          
          // Replace the last message with the complete response
          setChatMessages(prev => {
            const updatedMessages = [...prev];
            // Find the index of the message with our current human query
            const indexToUpdate = updatedMessages.findIndex(
              msg => msg.human === currentMessage && !msg.ai
            );
            
            if (indexToUpdate !== -1) {
              // Update this message with the AI response
              updatedMessages[indexToUpdate] = {
                human: currentMessage,
                ai: apiResponse.ai
              };
            }
            
            return updatedMessages;
          });
        } else {
          // API returns the full conversation history
          // Replace our chat history with the server's version
          console.log("Received full chat history from API:", data.messages);
          setChatMessages(data.messages);
        }
      } else {
        throw new Error("Geçersiz yanıt formatı");
      }
    } catch (error) {
      console.error('Chat error:', error);
      
      // Update the last message with an error
      setChatMessages(prev => {
        const updatedMessages = [...prev];
        // Find the index of our current message
        const indexToUpdate = updatedMessages.findIndex(
          msg => msg.human === currentMessage && !msg.ai
        );
        
        if (indexToUpdate !== -1) {
          // Update this message with an error
          updatedMessages[indexToUpdate] = {
            human: currentMessage,
            ai: "Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin."
          };
        }
        
        return updatedMessages;
      });
      
      setError("Sohbet yanıtı alınamadı. Lütfen tekrar deneyin.");
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Upload the file immediately when selected
      setUploading(true);
      setError(null);
      
      try {
        // Upload file to server
        await uploadFile(selectedFile);
        setUploading(false);
        console.log("File uploaded successfully");
      } catch (error) {
        console.error('File upload error:', error);
        setUploading(false);
        setError("Dosya yükleme sırasında bir hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
      
      // Upload the file immediately when dropped
      setUploading(true);
      setError(null);
      
      try {
        // Upload file to server
        await uploadFile(droppedFile);
        setUploading(false);
        console.log("File uploaded successfully");
      } catch (error) {
        console.error('File upload error:', error);
        setUploading(false);
        setError("Dosya yükleme sırasında bir hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = () => {
    setFile(null);
    setInsights([]);
    setChatMessages([]);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <header className="mb-8 flex flex-col items-center">
        {/* Logo */}
        <div className="mb-4 w-48 h-16 flex items-center justify-center">
          <img src="/argevim_logo.png" alt="Şirket Logo" className="max-h-full" />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">YZ ile Satış Verisi Analizi</h1>
        <p className="text-gray-600">Yapay zeka destekli içgörüler için satış verilerinizi yükleyin</p>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload">Veri Yükle</TabsTrigger>
          <TabsTrigger value="insights" disabled={insights.length === 0}>Önemli Bulgular</TabsTrigger>
          <TabsTrigger value="chat" disabled={!file}>Yapay Zeka ile Sohbet</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Satış Verilerini Yükle</CardTitle>
              <CardDescription>
                Satış verilerinizi içeren Excel dosyanızı yükleyin
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!file ? (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center"
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                >
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Excel dosyanızı sürükleyip bırakın veya dosya seçmek için tıklayın</p>
                  <Input 
                    type="file" 
                    accept=".xlsx,.xls,.csv" 
                    className="max-w-xs mx-auto"
                    onChange={handleFileChange}
                  />

                  <div className="mt-4 flex items-center justify-center gap-2">
                    <div className="h-px bg-gray-200 w-16" />
                    <span className="text-sm text-gray-400">veya</span>
                    <div className="h-px bg-gray-200 w-16" />
                  </div>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={handleLoadSampleData}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Örnek Veriyi Otomatik Yükle
                  </Button>
                  <p className="text-xs text-gray-400 mt-2">Demo amaçlı hazır satış verisiyle deneyin</p>

                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FileIcon className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={removeFile}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {file && !uploading && !analyzing && (
                <Alert className="mt-4 bg-blue-50 border-blue-200">
                  <AlertTitle>Dosyanız Hazır</AlertTitle>
                  <AlertDescription>
                    <p>Dosyanız yüklendi. İsterseniz veri analizini başlatabilir veya doğrudan yapay zeka ile sohbet edebilirsiniz.</p>
                    <div className="mt-2 flex space-x-2">
                      {insights.length === 0 && (
                        <Button onClick={handleUploadFile}>
                          Satış Verilerini Analiz Et
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        onClick={() => setActiveTab("chat")}
                      >
                        Yapay Zeka ile Sohbet Et
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {uploading && (
                <Alert className="mt-4">
                  <AlertTitle>Yükleniyor...</AlertTitle>
                  <AlertDescription>
                    Lütfen dosyanız yüklenirken bekleyin.
                  </AlertDescription>
                </Alert>
              )}

              {analyzing && (
                <Alert className="mt-4">
                  <AlertTitle>Analiz Ediliyor...</AlertTitle>
                  <AlertDescription>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
                          style={{ width: `${analysisProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm mt-2">
                        Yapay zeka satış verilerinizi analiz ediyor. Bu işlem birkaç dakika sürebilir.
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert className="mt-4 bg-red-50 border-red-200 text-red-800">
                  <AlertTitle>Hata</AlertTitle>
                  <AlertDescription>
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {insights.length > 0 && (
                <Alert className="mt-4 bg-green-50 border-green-200">
                  <AlertTitle>Analiz Tamamlandı!</AlertTitle>
                  <AlertDescription>
                    <p>Verilerinizi analiz ettik ve {insights.length} önemli bulgu bulduk.</p>
                    {activeTab !== "insights" && (
                      <Button 
                        className="mt-2" 
                        variant="outline" 
                        onClick={() => setActiveTab("insights")}
                      >
                        Bulguları Görüntüle
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Önemli Satış Bulguları</CardTitle>
              <CardDescription>
                Sigorta satış verilerinizden yapay zeka tarafından oluşturulan önemli bulgular
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {insights.map((insight, index) => (
                  <InsightCard key={index} insight={insight} />
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={switchToChat}
              >
                Takip Soruları Sor
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="chat" className="pt-4">
          <Card className="h-[calc(100vh-300px)] flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle>Yapay Zeka Asistanı ile Sohbet</CardTitle>
              <CardDescription>
                Satış verileriniz hakkında özel sorular sorun
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-grow overflow-hidden pt-2 pb-16 relative">
              <ScrollArea className="h-[calc(100vh-450px)] pt-2">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Başlamak için satış verileriniz hakkında bir soru sorun</p>
                    <div className="mt-2 space-y-2 mx-auto max-w-md">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left" 
                        onClick={() => setUserMessage("İstanbul şehrinde zamanla nasıl performans gösterdi?")}
                      >
                        İstanbul şehrinde zamanla nasıl performans gösterdi?
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left" 
                        onClick={() => setUserMessage("Hangi platformlar en iyi performansı gösterdi?")}
                      >
                        Hangi platformlar en iyi performansı gösterdi?
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left" 
                        onClick={() => setUserMessage("Cool Lime ürün satışları için trend neydi?")}
                      >
                        Cool Lime ürün satışları için trend neydi?
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pb-4">
                    {chatMessages.map((message, index) => (
                      <ChatMessage key={index} message={message} />
                    ))}
                    {chatLoading && (
                      <div className="p-3 rounded-lg bg-gray-50">
                        <div className="flex space-x-2">
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse"></div>
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
                          <div className="h-2 w-2 bg-gray-400 rounded-full animate-pulse delay-300"></div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Message input area - now part of CardContent */}
              <div className="border-t absolute bottom-0 left-0 right-0 p-4 bg-white">
                <div className="flex w-full max-w-[calc(100%-2rem)] mx-auto space-x-2">
                  <Input 
                    placeholder="Satış verileriniz hakkında bir soru sorun..." 
                    value={userMessage}
                    onChange={e => setUserMessage(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                  />
                  <Button onClick={handleSendMessage}>Gönder</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}